import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { useNavigate } from 'react-router';
import { useAuthentication } from '@connect-x-app/hybrid-state';
import { AdminConfigPermissions, SmartAction } from '@connect-x-app/shared-domain-models';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Spinner } from '@connect-x-app/web-ui';
import { Row } from '@tanstack/react-table';

import { ConfirmationModal } from '../../../../shared/confirmationModal/ConfirmationModal';
import { customModalStyles } from '../../../../shared/modal/ModalUtils';
import { CustomSmartActionsTable } from './components/customSmartActionsTable/CustomSmartActionsTable';
import { SystemSmartActionsTable } from './components/systemSmartActionsTable/SystemSmartActionsTable';
import { useSmartActionContentTable } from './hooks/useSmartActionContentTable';
import { useSmartActionGroups } from './hooks/useSmartActionGroups';

import './SmartActions.scss';

export const SmartActions = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { hasPermission } = useAuthentication();

  const canAddOrUpdate = useMemo(() => {
    return hasPermission(AdminConfigPermissions.WRITE);
  }, [hasPermission]);

  const canDelete = useMemo(() => {
    return hasPermission(AdminConfigPermissions.DELETE);
  }, [hasPermission]);

  const { customSmartActions, isLoading, refetch, systemSmartActions } = useSmartActionGroups();

  const { deleteButtonDisabled, deleteSmartActions, isDeleteSmartActionLoading, table } = useSmartActionContentTable({
    data: customSmartActions,
    refetchSmartActions: refetch,
    renderCheckbox: canDelete,
  });

  if (isLoading) {
    return <Spinner testId={ADMIN_TEST_IDS.smartActions.spinner}>{t('SHARED.LOADING_APPLICATION')}</Spinner>;
  }

  return (
    <div className="smart-actions-container" data-test={ADMIN_TEST_IDS.smartActions.screen}>
      <Modal isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)} style={customModalStyles}>
        <ConfirmationModal
          cancelButtonAction={() => {
            setIsModalOpen(false);
          }}
          cancelButtonLabel={t('SMART_ACTIONS_CONFIRMATION_MODAL_CANCEL_BUTTON_LABEL')}
          confirmButtonAction={async () => {
            await deleteSmartActions();
            setIsModalOpen(false);
          }}
          confirmButtonLabel={t('SMART_ACTIONS_CONFIRMATION_MODAL_CONFIRM_BUTTON_LABEL')}
          loading={isDeleteSmartActionLoading}
          title={t('SMART_ACTIONS_CONFIRMATION_MODAL_TITLE')}
        />
      </Modal>

      <div className="smart-actions-tables" data-test={ADMIN_TEST_IDS.smartActions.tables}>
        <CustomSmartActionsTable
          deleteButtonDisabled={deleteButtonDisabled}
          isAddButtonPresent={canAddOrUpdate}
          isDeleteButtonPresent={canDelete}
          onAdd={() => navigate('new')}
          onDelete={() => setIsModalOpen(true)}
          onRowClicked={(row: Row<SmartAction>) => navigate(`edit/${row.original.id}/`)}
          rowClickAllowed={canAddOrUpdate}
          table={table}
        />

        <SystemSmartActionsTable
          data={systemSmartActions}
          onRowClicked={(row: Row<SmartAction>) => navigate(`edit/${row.original.id}/`)}
        />
      </div>
    </div>
  );
};
